import ReactMarkdown from "react-markdown";
import { branding, intentMeta } from "../config/branding";
import { absoluteUrl } from "../services/api";
import { ChatMessage, KbStatus } from "../types/chat";
import { FeedbackButtons } from "./FeedbackButtons";
import { BotAvatar, UserAvatar } from "./Icons";

interface MessageBubbleProps {
  message: ChatMessage;
  sessionId?: string;
}

const KB_STATUS_BADGE: Record<KbStatus, { label: string; className: string } | null> = {
  documented: {
    label: "Documented fix available",
    className: "border-emerald-200 bg-emerald-50 text-emerald-800",
  },
  partial: {
    label: "Partial KB match",
    className: "border-amber-200 bg-amber-50 text-amber-900",
  },
  none: {
    label: "No documented fix",
    className: "border-rose-200 bg-rose-50 text-rose-800",
  },
  not_applicable: null,
};

/** Older answers still embed a prose coverage line; the badge replaces it. */
function stripKbCoverageLine(content: string): string {
  return content
    .split("\n")
    .filter((line) => !/^\s*(\*\*)?KB Coverage:(\*\*)?/i.test(line))
    .join("\n")
    .replace(/^\n+/, "")
    .trim();
}

function formatTime(date: Date) {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function MessageBubble({ message, sessionId }: MessageBubbleProps) {
  const isUser = message.role === "user";
  const intentLabel = message.intent ? intentMeta[message.intent].shortLabel : null;
  const kbBadge = !isUser && message.kb_status ? KB_STATUS_BADGE[message.kb_status] : null;
  const displayContent = isUser ? message.content : stripKbCoverageLine(message.content);

  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      {isUser ? <UserAvatar /> : <BotAvatar />}

      <div className={`max-w-[78%] ${isUser ? "items-end" : "items-start"} flex flex-col`}>
        <div className="mb-1 flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-700">
            {isUser ? branding.engineerLabel : branding.assistantName}
          </span>
          {intentLabel && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500">
              {intentLabel}
            </span>
          )}
          <span className="text-[10px] text-gray-400">{formatTime(message.timestamp)}</span>
        </div>

        <div
          className={`rounded-2xl px-4 py-3 shadow-sm ${
            isUser
              ? "rounded-tr-sm bg-brand-navy text-white"
              : "rounded-tl-sm border border-gray-200 bg-white text-gray-900"
          }`}
        >
          {kbBadge && (
            <div
              className={`mb-3 inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${kbBadge.className}`}
            >
              {kbBadge.label}
            </div>
          )}

          {message.images && message.images.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {message.images.map((img) => {
                const src = absoluteUrl(img.url || `/api/v1/uploads/${img.upload_id}`);
                return (
                  <a
                    key={img.upload_id}
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block h-24 w-24 overflow-hidden rounded-lg border border-white/30"
                  >
                    <img
                      src={src}
                      alt="Attached screenshot"
                      className="h-full w-full object-cover"
                    />
                  </a>
                );
              })}
            </div>
          )}

          {isUser ? (
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{displayContent}</p>
          ) : (
            <div className="chat-prose text-sm">
              <ReactMarkdown>{displayContent}</ReactMarkdown>
            </div>
          )}

          {!isUser && message.escalation_suggested && (
            <div className="mt-3 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">
              <svg className="h-4 w-4 shrink-0 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Escalation recommended — review before proceeding
            </div>
          )}

          {!isUser && message.confidence_note && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <svg className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-accent" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              {message.confidence_note}
            </div>
          )}

          {!isUser && message.sources && message.sources.length > 0 && (
            <details className="mt-3 group">
              <summary className="flex cursor-pointer list-none items-center gap-2 text-xs font-semibold text-brand-accent">
                <svg className="h-4 w-4 transition group-open:rotate-90" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                {message.kb_status === "none"
                  ? `${message.sources.length} loosely related article${
                      message.sources.length !== 1 ? "s" : ""
                    } — no documented fix`
                  : `${message.sources.length} knowledge base source${
                      message.sources.length !== 1 ? "s" : ""
                    }`}
              </summary>
              <ul className="mt-2 space-y-2">
                {message.sources.map((source, i) => (
                  <li
                    key={i}
                    className="rounded-lg border border-gray-100 bg-gradient-to-r from-slate-50 to-white p-3 text-xs"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-gray-800">{source.title}</div>
                      <span className="shrink-0 rounded-full bg-brand-accent/10 px-2 py-0.5 text-[10px] font-bold text-brand-navy">
                        {(source.score * 100).toFixed(0)}% match
                      </span>
                    </div>
                    <p className="mt-1.5 leading-relaxed text-gray-600">{source.content_preview}</p>
                    {source.source_path.startsWith("http") && (
                      <a
                        href={source.source_path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-brand-accent hover:underline"
                      >
                        {/lightning\.force\.com|salesforce\.com/i.test(source.source_path)
                          ? "Open in Salesforce ↗"
                          : /docs\.hidglobal\.com|hidglobal\.com/i.test(source.source_path)
                            ? "Open in HID Docs ↗"
                            : /atlassian\.net/i.test(source.source_path)
                              ? "Open in Jira/Confluence ↗"
                              : "open source ↗"}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {!isUser && (
            <FeedbackButtons
              messageId={message.id}
              sessionId={message.session_id || sessionId || ""}
            />
          )}
        </div>
      </div>
    </div>
  );
}
